import { Container } from '@mui/material';
import { Formik, FormikProps } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { LoadingIndicator, SystemMinimumDate, createPivotDateTimeFromDate } from 'src/common';
import { MonthlyRunMode, ReportFileNameType, ScheduledEventInput, ServiceType } from 'src/features/reporting/store/types';
import { Props } from '../container';
import { SchedulerParams } from '../store/types';
import { ReportingSchedulerStepper } from './ReportingSchedulerStepper';
import { SchedulerValidationSchema } from './ReportingSchedulerStepperValidation';

export const EditScheduledReportComponent = (props: Props) => {
  const history = useHistory();

  const { id } = useParams<SchedulerParams>();

  if (!id) {
    return <Redirect to="/reporting?tab=scheduler" />;
  }

  useEffect(() => {
    props.setInceptionDate(DateTime.fromISO(SystemMinimumDate).toISODate());
    props.resetReports();
    props.fetchScheduledEventById(id);
  }, [id]);

  const event = props.scheduler.editingEvent;

  const handleSubmit = (values: ScheduledEventInput, trigger: boolean) => {
    const recurrenceRuleId = event?.recurrenceRule?.id ?? undefined;
    const eventInput: ScheduledEventInput = {
      ...values,
    };

    if (values.recurrenceRule && recurrenceRuleId) {
      eventInput.recurrenceRule = {
        ...values.recurrenceRule,
        id: recurrenceRuleId,
      };
    }

    // Normalise the dates into pivot dates in case they somehow end up storing a local date.
    eventInput.startOn = createPivotDateTimeFromDate(eventInput.startOn).toJSDate();
    if (eventInput.endOn) {
      eventInput.endOn = createPivotDateTimeFromDate(eventInput.endOn).toJSDate();
    }

    props
      .updateScheduledEvent({ id: id, input: eventInput })
      .unwrap()
      .then(() => {
        if (trigger) {
          props.triggerScheduledEvent({ id: id }).then(() => {
            history.replace('/reporting?tab=scheduler');
          });
        } else {
          history.replace('/reporting?tab=scheduler');
        }
      });
  };

  const handleDiscardChanges = (formikProps: FormikProps<ScheduledEventInput>) => {
    props.setSelectedReportingParameter(null);
    props.setSavedParameter(null);

    const reportParameters = event?.reportParameters ?? undefined;
    formikProps.resetForm();

    if (reportParameters) {
      props.setReportParameters(reportParameters);
    }
  };

  return (
    <Container maxWidth={false} sx={{ padding: 6 }}>
      <LoadingIndicator progress={props.fetchScheduledEventByIdProgress || event === undefined || event === null}>
        {event !== null && event !== undefined && (
          <Formik<ScheduledEventInput>
            initialValues={{
              name: event.name ?? '',
              description: event.description ?? '',
              recurrenceType: event.recurrenceType ?? 'Daily',
              occurrences: event.occurrences ?? 0,
              reportFileNameTemplate: event.reportFileNameTemplate || ReportFileNameType.NonMerged.displayName,
              displayOnInvestorPortal: event.displayOnInvestorPortal ?? false,
              startOn: new Date(event.startOn),
              clientSearchFilter: {
                afslIds: event.clientSearchFilter?.afslIds ?? [],
                clientIds: event.clientSearchFilter?.clientIds ?? [],
                adviserIds: event.clientSearchFilter?.adviserIds ?? [],
                practiceIds: event.clientSearchFilter?.practiceIds ?? [],
                serviceType: event.clientSearchFilter?.serviceType ?? ServiceType.Mda.name,
                clientTypes: event.clientSearchFilter?.clientTypes ?? [],
                groupIds: event.clientSearchFilter?.groupIds ?? [],
                statuses: event.clientSearchFilter?.statuses ?? [],
                modelId: event.clientSearchFilter?.modelId,
              },
              recurrenceRule: event.recurrenceRule,
              emailParameters: event.emailParameters ?? undefined,
              reportParameters: event.reportParameters ?? undefined,
              endOn: event.endOn ? new Date(event.endOn) : undefined,
              allowTerminatedClients: event.allowTerminatedClients ?? false,
              monthlyRunModeId: event.monthlyRunModeId ?? MonthlyRunMode.ExactDay,
            }}
            validationSchema={SchedulerValidationSchema}
            validateOnChange={false}
            validateOnMount={false}
            validateOnBlur={false}
            enableReinitialize
            validate={async (values) => {
              try {
                await SchedulerValidationSchema.validate(values, {
                  abortEarly: false,
                  context: { recurrenceType: values.recurrenceType },
                });
              } catch (e) {}
            }}
            onSubmit={(values: ScheduledEventInput) => handleSubmit(values, false)}
          >
            {(formikProps: FormikProps<ScheduledEventInput>) => (
              <ReportingSchedulerStepper
                discardChanges={handleDiscardChanges}
                editing
                {...props}
                handleSubmit={handleSubmit}
                formikProps={formikProps}
                initialStep={0}
              />
            )}
          </Formik>
        )}
      </LoadingIndicator>
    </Container>
  );
};
